import type { AuditActivityItem } from '@/services';
import { SelectFilterField } from '@/shared/ui';
import { f, useFilters } from '@/shared/utils';
import {
  Button,
  DataTable,
  DataTableColumn,
  Field,
  Menu,
  RadioGroup,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { Download, Eye, Info, ScrollText, UserSearch } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useAuditActivities, useAuditExport } from '../hooks';
import {
  AUDIT_ACTION_OPTIONS,
  collapseBatchActivities,
  AUDIT_SCOPE_MODE_OPTIONS,
  AUDIT_SUBJECT_TYPE_OPTIONS,
  buildAuditQueryParams,
  describeActiveScope,
  describeMissingScope,
  formatActivitySummary,
  formatActor,
  formatActorSource,
  formatOccurredAt,
  formatSubjectType,
  todayAuditDay,
  type AuditFiltersState,
  type AuditScopeMode,
} from '../utils';
import { AuditActorCombobox } from './audit-actor-combobox.component';
import { AuditDetailModal } from './audit-detail.modal';

const EMPTY = '—';

/** Chave estável de uma linha: PK + SK da trilha. */
const rowKey = (item: AuditActivityItem) =>
  `${item.subject}#${item.activityId}`;

/**
 * Botão de expandir/recolher um grupo de lote.
 *
 * A linha-cabeçalho precisa oferecer as duas ações: só o "+N" deixava a
 * operação sem como fechar depois de aberta. `text-left` porque o padrão do
 * `<button>` é centralizar, e ao quebrar em duas linhas ele destoava da coluna.
 */
function BatchToggleButton({
  batchId,
  hidden,
  size = 'sm',
  onToggle,
}: {
  batchId: string;
  hidden?: number;
  size?: 'sm' | 'md';
  onToggle: (_batchId: string) => void;
}) {
  return (
    <button
      type='button'
      onClick={() => onToggle(batchId)}
      className={`self-start text-left font-bold text-alba-blue-500 underline-offset-4 hover:underline ${
        size === 'md' ? 'text-sm' : 'text-xs'
      }`}
    >
      {hidden
        ? `+ ${hidden} registro(s) desta operação`
        : 'Recolher esta operação'}
    </button>
  );
}

function ActorSourceTag({ actorSource }: { actorSource: string }) {
  const isSystem = actorSource === 'SYSTEM';

  return (
    <Tag.Root
      variant='outline'
      colorScheme={isSystem ? 'gray' : 'blue'}
      size='sm'
    >
      <Tag.Label>{formatActorSource(actorSource)}</Tag.Label>
    </Tag.Root>
  );
}

/**
 * Trilha de auditoria — quem fez o que e quando, em todo o Portal.
 *
 * A API não lista "tudo": cada consulta precisa de um recorte que case com uma
 * chave do DynamoDB, e ela escolhe o índice por PRECEDÊNCIA (entidade > autor
 * > dia), ignorando os outros recortes em silêncio. Por isso a tela trabalha
 * com UM recorte por vez (e diz qual está ativo) em vez de oferecer filtros
 * que pareceriam combináveis e não são. O padrão é o dia de hoje.
 */
export function AuditPage() {
  const { filters, debouncedFilters, setFilters } = useFilters({
    mode: f.string().withDefault('day'),
    // O dia default é o de hoje em UTC — é assim que o BFF indexa a trilha.
    day: f.string().withDefault(todayAuditDay()),
    // Sem debounce: o valor sai de uma seleção no combobox, não de digitação.
    actorSub: f.string().withDefault(''),
    // Só exibição: a consulta usa o `actorSub`.
    actorLabel: f.string().withDefault(''),
    subjectType: f.string().withDefault(''),
    subjectId: f.string().withDefault('').withDebounce(500),
    action: f.string().withDefault(''),
  });

  const [selected, setSelected] = useState<AuditActivityItem | null>(null);

  const mode = (filters.mode || 'day') as AuditScopeMode;

  /** Estado já debounced, que é o que vira request. */
  const queryFilters = useMemo<AuditFiltersState>(
    () => ({
      mode: (debouncedFilters.mode || 'day') as AuditScopeMode,
      day: debouncedFilters.day,
      actorSub: debouncedFilters.actorSub,
      subjectType: debouncedFilters.subjectType,
      subjectId: debouncedFilters.subjectId,
      action: debouncedFilters.action,
    }),
    [debouncedFilters]
  );

  const params = useMemo(
    () => buildAuditQueryParams(queryFilters),
    [queryFilters]
  );
  const missingScope = describeMissingScope(queryFilters);

  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAuditActivities(params);

  const exportMutation = useAuditExport();

  /** Lotes expandidos: por padrão a operação em massa ocupa uma linha só. */
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(
    () => new Set()
  );

  const toggleBatch = useCallback((batchId: string) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  }, []);

  const items = useMemo(
    () => data?.pages.flatMap(page => page.items) ?? [],
    [data]
  );

  // Uma ativação em 200 corretoras grava 201 registros: sem recolher, a visão
  // do dia vira 201 linhas quase idênticas.
  const { rows, hiddenByRowId, batchHeaderById } = useMemo(
    () => collapseBatchActivities(items, expandedBatches),
    [items, expandedBatches]
  );

  /**
   * Nome do autor filtrado. As linhas carregadas são a fonte mais confiável (a
   * API resolve o nome na leitura); sem linhas — autor sem ações — vale o nome
   * guardado na URL no momento da escolha. Ele fica na URL, e não em estado,
   * porque trocar o filtro remonta a tela e o estado se perderia.
   */
  const actorSub = filters.actorSub.trim();
  const knownActorLabel = useMemo(() => {
    if (!actorSub) return undefined;
    const row = items.find(item => item.actorSub === actorSub);
    const rowLabel = row ? formatActor(row) : undefined;
    if (rowLabel && rowLabel !== actorSub) return rowLabel;
    return filters.actorLabel.trim() || undefined;
  }, [actorSub, filters.actorLabel, items]);

  /** Troca de recorte zera o que não se aplica, para não confundir a leitura. */
  const changeMode = (next: AuditScopeMode) => {
    setFilters({
      mode: next,
      day: next === 'day' ? filters.day || todayAuditDay() : filters.day,
    });
  };

  const selectActor = (nextActorSub: string, label?: string) =>
    setFilters({ actorSub: nextActorSub, actorLabel: label ?? '' });

  /** Atalho das linhas: pular direto para a trilha daquele autor. */
  const filterByActor = (item: AuditActivityItem) => {
    const label = formatActor(item);
    setFilters({
      mode: 'actor',
      actorSub: item.actorSub,
      actorLabel: label === item.actorSub ? '' : label,
    });
  };

  /** Atalho das linhas: pular direto para o histórico daquela entidade. */
  const filterBySubject = (item: AuditActivityItem) =>
    setFilters({
      mode: 'subject',
      subjectType: item.subjectType,
      subjectId: item.subjectId,
    });

  const columns = useMemo<DataTableColumn<AuditActivityItem>[]>(
    () => [
      {
        id: 'occurredAt',
        header: 'Data/hora',
        size: 140,
        sortable: false,
        cell: ({ row }: { row: { original: AuditActivityItem } }) => (
          <span className='whitespace-nowrap text-sm text-alba-gray-700'>
            {formatOccurredAt(row.original.occurredAt)}
          </span>
        ),
      },
      {
        id: 'actor',
        header: 'Autor',
        size: 200,
        sortable: false,
        cell: ({ row }: { row: { original: AuditActivityItem } }) => {
          const item = row.original;
          return (
            <div className='flex flex-col gap-1'>
              <span className='font-medium text-alba-black-500 [overflow-wrap:anywhere]'>
                {formatActor(item)}
              </span>
              <div>
                <ActorSourceTag actorSource={item.actorSource} />
              </div>
            </div>
          );
        },
      },
      {
        id: 'action',
        header: 'Ação',
        size: 190,
        sortable: false,
        cell: ({ row }: { row: { original: AuditActivityItem } }) => (
          <span className='text-sm text-alba-black-500'>
            {row.original.actionLabel}
          </span>
        ),
      },
      {
        id: 'subject',
        header: 'Entidade',
        size: 200,
        sortable: false,
        cell: ({ row }: { row: { original: AuditActivityItem } }) => {
          const item = row.original;
          return (
            <div className='flex flex-col gap-1'>
              <span className='text-sm text-alba-gray-700 [overflow-wrap:anywhere]'>
                {item.subjectLabel || item.subjectId}
              </span>
              <span className='text-xs text-alba-gray-600'>
                {formatSubjectType(item.subjectType)}
              </span>

              {/* Os registros individuais do lote continuam gravados — é o que
                  responde "o que houve NESTA corretora". Aqui eles só ficam
                  recolhidos para a visão do dia não virar 201 linhas iguais. */}
              {batchHeaderById[item.activityId] ? (
                <BatchToggleButton
                  batchId={batchHeaderById[item.activityId]}
                  hidden={hiddenByRowId[item.activityId]}
                  onToggle={toggleBatch}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'summary',
        header: 'Detalhes',
        size: 320,
        sortable: false,
        cell: ({ row }: { row: { original: AuditActivityItem } }) => {
          const summary = formatActivitySummary(row.original);
          return (
            <span
              className='line-clamp-2 text-sm text-alba-gray-700 [overflow-wrap:anywhere]'
              title={summary}
            >
              {summary || EMPTY}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Ações',
        size: 50,
        sortable: false,
        cell: ({ row }: { row: { original: AuditActivityItem } }) => {
          const item = row.original;
          return (
            <div className='flex justify-end'>
              <Menu.Root>
                <Menu.Trigger />
                <Menu.Content>
                  <Menu.Item
                    value='detail'
                    icon={<Eye size={16} />}
                    onSelect={() => setSelected(item)}
                  >
                    Ver detalhes
                  </Menu.Item>
                  <Menu.Item
                    value='actor'
                    icon={<UserSearch size={16} />}
                    onSelect={() => filterByActor(item)}
                  >
                    Ver ações deste autor
                  </Menu.Item>
                  <Menu.Item
                    value='subject'
                    icon={<ScrollText size={16} />}
                    onSelect={() => filterBySubject(item)}
                  >
                    Ver histórico desta entidade
                  </Menu.Item>
                </Menu.Content>
              </Menu.Root>
            </div>
          );
        },
      },
    ],
    // `filterByActor`/`filterBySubject` só dependem de `setFilters`, que é
    // estável no nuqs.

    // As células leem o agrupamento: com `[]` elas ficavam presas ao primeiro
    // render (sem dados) e o botão de expandir nunca aparecia.
    [batchHeaderById, hiddenByRowId, toggleBatch]
  );

  return (
    <>
      <AuditDetailModal activity={selected} onClose={() => setSelected(null)} />

      <div className='flex min-w-0 flex-col gap-4'>
        <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-alba-black-500'>
              Trilha de auditoria
            </h2>
            <p className='mt-1 text-sm text-alba-gray-600'>
              Quem fez o que e quando nas operações administrativas do Portal. O
              registro é permanente e não pode ser editado.
            </p>
          </div>

          <Button
            variant='outline'
            className='w-full md:w-auto'
            disabled={!params}
            loading={exportMutation.isPending}
            title='Baixar em CSV todas as linhas do recorte atual, não só as carregadas na tela'
            onClick={() => params && exportMutation.mutate(params)}
          >
            <Download className='h-5 w-5 shrink-0' strokeWidth={1.8} />
            Exportar CSV
          </Button>
        </div>

        <section className='flex flex-col gap-4 rounded-2xl border border-alba-gray-200 bg-white p-4'>
          <div className='flex flex-col gap-2'>
            <p className='text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
              Consultar por
            </p>
            {/* Recorte único por consulta: a API casa cada um com um índice
                diferente e ignora os demais. Radio deixa isso explícito —
                checkbox sugeriria que dá para somar. */}
            <RadioGroup.Root
              value={mode}
              orientation='horizontal'
              className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2'
              onValueChange={details =>
                changeMode(details.value as AuditScopeMode)
              }
            >
              {AUDIT_SCOPE_MODE_OPTIONS.map(option => (
                <RadioGroup.Item key={option.value} value={option.value}>
                  <RadioGroup.ItemControl>
                    <RadioGroup.Indicator />
                  </RadioGroup.ItemControl>
                  <RadioGroup.ItemText>{option.label}</RadioGroup.ItemText>
                  <RadioGroup.ItemHiddenInput />
                </RadioGroup.Item>
              ))}
            </RadioGroup.Root>
          </div>

          {/* Alinhamento pelo TOPO: as colunas têm alturas diferentes (só a do
              autor tem texto de ajuda) e `items-end` fazia os rótulos
              desencontrarem. */}
          <div className='flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-start md:gap-4'>
            {mode === 'day' && (
              <Field.Root className='w-full md:max-w-[220px]'>
                <Field.Label>Dia</Field.Label>
                <Field.Input
                  type='date'
                  value={filters.day}
                  onChange={event => setFilters({ day: event.target.value })}
                  onKeyDown={event => event.stopPropagation()}
                />
              </Field.Root>
            )}

            {mode === 'actor' && (
              <AuditActorCombobox
                value={filters.actorSub}
                knownLabel={knownActorLabel}
                onChange={selectActor}
              />
            )}

            {mode === 'subject' && (
              <>
                <Field.Root className='w-full md:max-w-[220px]'>
                  <Field.Label>Tipo da entidade</Field.Label>
                  <SelectFilterField
                    name='subjectType'
                    type='string'
                    value={filters.subjectType}
                    items={AUDIT_SUBJECT_TYPE_OPTIONS}
                    placeholder='Tipo da entidade'
                    onChange={value =>
                      setFilters({ subjectType: String(value ?? '') })
                    }
                  />
                </Field.Root>

                <Field.Root className='w-full md:max-w-[320px]'>
                  <Field.Label>Id da entidade</Field.Label>
                  <Field.Input
                    value={filters.subjectId}
                    placeholder='ex.: PTY10000002858042'
                    autoComplete='off'
                    onChange={event =>
                      setFilters({ subjectId: event.target.value })
                    }
                    onKeyDown={event => event.stopPropagation()}
                  />
                </Field.Root>
              </>
            )}

            <Field.Root className='w-full md:max-w-[260px]'>
              <Field.Label>Ação</Field.Label>
              <SelectFilterField
                name='action'
                type='string'
                value={filters.action}
                items={AUDIT_ACTION_OPTIONS}
                placeholder='Todas as ações'
                onChange={value => setFilters({ action: String(value ?? '') })}
              />
            </Field.Root>
          </div>

          {/* O aviso é permanente de propósito: sem ele o administrador
              acharia que a tela mostra "tudo", quando mostra um recorte. */}
          <p className='flex items-start gap-2 rounded-lg bg-alba-gray-100 px-3 py-2 text-sm text-alba-gray-700'>
            <Info
              className='mt-0.5 h-4 w-4 shrink-0 text-alba-blue-500'
              strokeWidth={1.8}
            />
            <span>
              {missingScope
                ? `${missingScope} A consulta só é feita com um recorte completo.`
                : describeActiveScope(queryFilters, knownActorLabel)}
            </span>
          </p>

          {filters.action && (
            <p className='text-xs text-alba-gray-600'>
              O filtro por ação é aplicado sobre cada página lida, então uma
              página pode vir vazia mesmo havendo mais registros adiante — siga
              em “Carregar mais”.
            </p>
          )}
        </section>

        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            {error instanceof Error
              ? error.message
              : 'Erro ao carregar a trilha de auditoria.'}
          </div>
        ) : (
          <>
            <div className='space-y-3 md:hidden'>
              {isLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={index}
                      className='h-40 animate-pulse rounded-2xl bg-alba-gray-200'
                    />
                  ))
                : rows.map(item => {
                    const summary = formatActivitySummary(item);

                    return (
                      <article
                        key={rowKey(item)}
                        className='rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                      >
                        <div className='flex items-start justify-between gap-3'>
                          <div className='min-w-0'>
                            <p className='font-gotham font-bold text-alba-black-500 [overflow-wrap:anywhere]'>
                              {item.actionLabel}
                            </p>
                            <p className='mt-1 text-xs text-alba-gray-600'>
                              {formatOccurredAt(item.occurredAt)}
                            </p>
                          </div>
                          <Menu.Root>
                            <Menu.Trigger />
                            <Menu.Content>
                              <Menu.Item
                                value='detail'
                                icon={<Eye size={16} />}
                                onSelect={() => setSelected(item)}
                              >
                                Ver detalhes
                              </Menu.Item>
                              <Menu.Item
                                value='actor'
                                icon={<UserSearch size={16} />}
                                onSelect={() => filterByActor(item)}
                              >
                                Ver ações deste autor
                              </Menu.Item>
                              <Menu.Item
                                value='subject'
                                icon={<ScrollText size={16} />}
                                onSelect={() => filterBySubject(item)}
                              >
                                Ver histórico desta entidade
                              </Menu.Item>
                            </Menu.Content>
                          </Menu.Root>
                        </div>

                        <div className='mt-3 grid grid-cols-1 gap-3 border-t border-alba-gray-200 pt-3'>
                          <div>
                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                              Autor
                            </p>
                            <p className='text-sm text-alba-gray-700 [overflow-wrap:anywhere]'>
                              {formatActor(item)}
                            </p>
                            <div className='mt-1'>
                              <ActorSourceTag actorSource={item.actorSource} />
                            </div>
                          </div>

                          <div>
                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                              Entidade
                            </p>
                            <p className='text-sm text-alba-gray-700 [overflow-wrap:anywhere]'>
                              {formatSubjectType(item.subjectType)} ·{' '}
                              {item.subjectLabel || item.subjectId}
                            </p>
                          </div>

                          {summary && (
                            <div>
                              <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                                Detalhes
                              </p>
                              <p className='line-clamp-3 text-sm text-alba-gray-700 [overflow-wrap:anywhere]'>
                                {summary}
                              </p>
                            </div>
                          )}

                          {batchHeaderById[item.activityId] ? (
                            <BatchToggleButton
                              batchId={batchHeaderById[item.activityId]}
                              hidden={hiddenByRowId[item.activityId]}
                              size='md'
                              onToggle={toggleBatch}
                            />
                          ) : null}
                        </div>
                      </article>
                    );
                  })}

              {!isLoading && !rows.length && (
                <div className='rounded-2xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  {params
                    ? 'Nenhum registro neste recorte.'
                    : 'Complete o recorte para consultar a trilha.'}
                </div>
              )}
            </div>

            <div className='hidden min-w-0 overflow-hidden rounded-2xl md:block'>
              <DataTable
                // `totalPages={0}` desliga a paginação do design system: aqui a
                // navegação é por cursor ("Carregar mais"), não por página.
                totalPages={0}
                className={[
                  'min-w-0',
                  '[&_th]:px-2 [&_td]:px-2',
                  '[&_th:first-child]:pl-4 [&_td:first-child]:pl-4',
                  '[&_th:last-child]:pr-4 [&_td:last-child]:pr-4',
                ].join(' ')}
                loading={isLoading}
                columns={columns}
                data={rows}
                emptyMessage={
                  params
                    ? 'Nenhum registro neste recorte.'
                    : 'Complete o recorte para consultar a trilha.'
                }
              />
            </div>

            {/* Respiro no fim da lista: sem ele o contador encosta na borda
                inferior da janela no mobile. */}
            <div className='mb-6 flex flex-col items-center gap-2'>
              {items.length > 0 && (
                <p className='text-xs text-alba-gray-600'>
                  {items.length}{' '}
                  {items.length === 1
                    ? 'registro carregado'
                    : 'registros carregados'}
                </p>
              )}

              {hasNextPage && (
                <Button
                  variant='outline'
                  className='w-full md:w-auto'
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  Carregar mais
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
